import React, { useRef, useCallback, useEffect, useState } from 'react'
import { useToast, VStack, Badge, HStack } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import ReactTextareaAutosize from 'react-textarea-autosize'
import axios from 'axios'

import { useAuth } from '../../../contexts/Auth'
import { useProfile } from '../../../contexts/Profile'
import { useEncoderStateMachine } from '../../../hooks/useEncoderStateMachine'
import { useEtcbcValidation } from '../../../hooks/useEtcbcValidation'
import { useEncoderDraft } from '../../../hooks/useEncoderDraft'
import { useEncoderRealtime } from '../../../hooks/useEncoderRealtime'
import {
    EtcbcEncoding,
    EncodingContainer,
    OptionLabel,
    TextAreaContainer,
    constructRequestParams,
} from '../../../pages/Reader'
import { convertEncodingToUnicode } from '../../Decoder'

import { EncoderButtons } from './EncoderButtons'
import { EncoderDiffDisplay } from './EncoderDiffDisplay'
import { EncoderValidationErrors } from './EncoderValidationErrors'
import {
    submitEncoding,
    updateSubmission,
    cancelSubmission,
    approveSubmission,
} from './EncoderActions'
import { getSubmitterName } from './EncoderStateMachine'
import { IWordLevelAnalysis } from '../WordLevelAnalysis'

interface FormValues {
    proposedvalue: string
}

interface ValidationError {
    code: string
    error: string
}

export const Encoder: React.FC<{
    encoding: EtcbcEncoding
    westernScript: boolean
    suggestedChange: {
        id: string
        body: string
        user_id: {
            id: string
            first_name: string
            last_name: string
        }
    } | null
    textEncoding: string
    showErrorsByDefault: boolean
    batchValidationErrors: ValidationError[]
}> = ({
    encoding,
    westernScript,
    suggestedChange,
    textEncoding,
    showErrorsByDefault,
    batchValidationErrors,
}) => {
    const toast = useToast()
    const textAreaRef = useRef<HTMLTextAreaElement>(null)
    const session = useAuth()
    const { role } = useProfile()

    // Initialize state machine with initial values
    const {
        state,
        dispatch,
        canSubmit,
        canUpdate,
        canApprove,
        canCancel,
        canUndo,
        showDiffPreview,
    } = useEncoderStateMachine(
        encoding.body,
        suggestedChange,
        session?.user?.id,
        role,
        false // Will be updated by form errors
    )

    // React Hook Form setup
    const {
        register,
        watch,
        setValue,
        formState: { errors, touchedFields, isValidating },
        trigger,
    } = useForm<FormValues>({
        defaultValues: {
            proposedvalue: state.currentValue,
        },
        mode: 'onChange',
    })

    const proposedValue = watch('proposedvalue')
    const [isServerValidationPending, setIsServerValidationPending] =
        useState(false)
    const lastValidatedValueRef = useRef(state.currentValue)
    const isUserInputRef = useRef(false)

    // Sync form value when state.currentValue changes (e.g., draft loaded)
    useEffect(() => {
        setValue('proposedvalue', state.currentValue)
    }, [state.currentValue, setValue])

    // Custom validation function
    const validateProposedValue = useCallback(
        async (value: string): Promise<string | undefined> => {
            if (!value) {
                return 'Proposed value cannot be empty'
            }

            try {
                const data = constructRequestParams(textEncoding, [value])
                const result = await axios.post(
                    'https://etcbc.vu.nl/api/wla',
                    data
                )

                const realisedWords: string[] = []
                ;(
                    result.data?.result[0] as Array<Array<IWordLevelAnalysis>>
                )?.forEach((wordgroup) => {
                    let concatWord = ''
                    wordgroup.forEach((word) => {
                        concatWord += word.realisation
                    })
                    realisedWords.push(concatWord)
                })

                if (
                    convertEncodingToUnicode(realisedWords.join(' ')) !==
                    convertEncodingToUnicode(value)
                ) {
                    return 'Proposed encoding does not match surface text'
                }
            } catch {
                // Validation API failed, allow submission
            }

            return undefined
        },
        [textEncoding]
    )

    // Register field with validation
    const { ref: registerRef, ...registerRest } = register('proposedvalue', {
        validate: validateProposedValue,
    })

    // Realtime subscription for external changes
    useEncoderRealtime(encoding.id, state.submissionId, dispatch)

    // ETCBC validation hook
    const { validateValue } = useEtcbcValidation(textEncoding, dispatch)

    // Draft auto-save hook
    const { saveDraft, deleteDraft } = useEncoderDraft(
        encoding.id,
        session?.user?.id,
        dispatch
    )

    const requestServerValidation = useCallback(
        (value: string) => {
            if (
                value === lastValidatedValueRef.current &&
                !isServerValidationPending
            ) {
                return
            }
            setIsServerValidationPending(true)
            validateValue(value)
        },
        [validateValue, isServerValidationPending]
    )

    // Handle textarea change
    const handleTextareaChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value

            // Update state machine
            isUserInputRef.current = true
            dispatch({
                type: 'TEXT_CHANGED',
                value: newValue,
            })

            // Trigger validation
            requestServerValidation(newValue)

            // Auto-save draft if not in pending approval
            if (
                state.status !== 'PENDING_APPROVAL' &&
                newValue !== state.originalValue
            ) {
                saveDraft(newValue)
            }

            if (
                state.status !== 'PENDING_APPROVAL' &&
                newValue === state.originalValue
            ) {
                deleteDraft()
            }
        },
        [
            dispatch,
            requestServerValidation,
            state.status,
            state.originalValue,
            saveDraft,
            deleteDraft,
        ]
    )

    // Validate draft/server-backed changes on load or external updates
    useEffect(() => {
        if (isUserInputRef.current) {
            isUserInputRef.current = false
            return
        }
        if (state.currentValue === lastValidatedValueRef.current) {
            return
        }

        requestServerValidation(state.currentValue)
        trigger('proposedvalue')
    }, [state.currentValue, requestServerValidation, trigger])

    useEffect(() => {
        if (!isServerValidationPending) return
        lastValidatedValueRef.current = state.currentValue
        setIsServerValidationPending(false)
    }, [
        state.serverValidationErrors,
        state.currentValue,
        isServerValidationPending,
    ])

    // Use batch validation errors when showErrorsByDefault is enabled
    useEffect(() => {
        if (showErrorsByDefault) {
            // Only use batch validation if the current value matches the original
            // (i.e., user hasn't made changes yet)
            if (state.currentValue === state.originalValue) {
                dispatch({
                    type: 'VALIDATION_COMPLETED',
                    errors: batchValidationErrors,
                })
                // Also trigger form validation to show formik errors
                trigger('proposedvalue')
            }
        } else {
            // Clear all errors when toggle is disabled
            dispatch({
                type: 'VALIDATION_COMPLETED',
                errors: [],
            })
        }
    }, [showErrorsByDefault, batchValidationErrors, dispatch, trigger, state.currentValue, state.originalValue])

    // Action handlers
    const handleSubmit = useCallback(async () => {
        if (!session?.user?.id) return
        await deleteDraft()
        await submitEncoding(encoding.id, session.user.id, state.currentValue, {
            toast,
            dispatch,
        })
    }, [
        encoding.id,
        session?.user?.id,
        state.currentValue,
        toast,
        dispatch,
        deleteDraft,
    ])

    const handleUpdate = useCallback(async () => {
        if (!state.submissionId) return
        await updateSubmission(state.submissionId, state.currentValue, {
            toast,
            dispatch,
        })
    }, [state.submissionId, state.currentValue, toast, dispatch])

    const handleCancelSubmission = useCallback(async () => {
        if (!state.submissionId) return
        await cancelSubmission(
            state.submissionId,
            { toast, dispatch },
            (field, value) => setValue(field as keyof FormValues, value),
            state.originalValue
        )
    }, [state.submissionId, state.originalValue, toast, dispatch, setValue])

    const handleApprove = useCallback(async () => {
        if (!session?.user?.id) return
        await approveSubmission(
            encoding.id,
            state.currentValue,
            session.user.id,
            suggestedChange,
            { toast, dispatch }
        )
    }, [
        encoding.id,
        state.currentValue,
        session?.user?.id,
        suggestedChange,
        toast,
        dispatch,
    ])

    const handleUndo = useCallback(async () => {
        await deleteDraft()
        setValue('proposedvalue', state.originalValue)
        dispatch({ type: 'RESET' })
    }, [deleteDraft, setValue, state.originalValue, dispatch])

    const submitterName = getSubmitterName(state.submittedBy)
    const hasFormErrors = Boolean(errors.proposedvalue)
    const hasServerValidatedCurrentValue =
        lastValidatedValueRef.current === state.currentValue
    const actualCanSubmit =
        canSubmit &&
        !hasFormErrors &&
        !isValidating &&
        hasServerValidatedCurrentValue &&
        !isServerValidationPending
    const actualCanUpdate =
        canUpdate &&
        !hasFormErrors &&
        !isValidating &&
        hasServerValidatedCurrentValue &&
        !isServerValidationPending

    return (
        <EncodingContainer
            style={{
                width: '100%',
                position: 'relative',
                minWidth: '100%',
                margin: '2rem 0 1rem',
            }}
        >
            <HStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <OptionLabel>Encoding</OptionLabel>
            </HStack>
            <VStack spacing="5">
                {/* Textarea */}
                <TextAreaContainer>
                    <form style={{ width: '100%' }}>
                        <ReactTextareaAutosize
                            {...registerRest}
                            ref={(e) => {
                                registerRef(e)
                                if (textAreaRef) {
                                    const ref =
                                        textAreaRef as React.MutableRefObject<HTMLTextAreaElement | null>
                                    ref.current = e
                                }
                            }}
                            name="proposedvalue"
                            minRows={4}
                            value={proposedValue}
                            onChange={(e) => {
                                registerRest.onChange(e)
                                handleTextareaChange(e)
                            }}
                        />
                    </form>
                </TextAreaContainer>
                {state.status === 'DRAFT_SAVED' && (
                    <Badge
                        variant="outline"
                        padding="0.4rem 0.65rem"
                        borderRadius="5px"
                        boxShadow="none"
                        backgroundColor="#fff"
                        color="#008b8b"
                        fontSize="xs"
                        sx={{
                            animation: 'draftSavedPulse 2s ease-out',
                            '@keyframes draftSavedPulse': {
                                '0%': {
                                    opacity: 0,
                                    transform: 'scale(0.95)',
                                    backgroundColor: '#e6fffa',
                                },
                                '20%': {
                                    opacity: 1,
                                    transform: 'scale(1.05)',
                                    backgroundColor: '#f3f5b2ff',
                                },
                                '40%': {
                                    transform: 'scale(1)',
                                    backgroundColor: '#e6fffa',
                                },
                                '100%': {
                                    opacity: 1,
                                    backgroundColor: '#fff',
                                },
                            },
                        }}
                    >
                        Draft saved
                    </Badge>
                )}
                {/* Diff display - show below textarea when there are changes */}
                {showDiffPreview && (
                    <EncoderDiffDisplay
                        originalBody={state.originalValue}
                        currentValue={state.currentValue}
                        submitterName={
                            state.status === 'PENDING_APPROVAL'
                                ? submitterName
                                : null
                        }
                    />
                )}

                {/* Validation errors */}
                <EncoderValidationErrors
                    formikError={errors.proposedvalue?.message}
                    serverErrors={state.serverValidationErrors}
                    touched={Boolean(touchedFields.proposedvalue)}
                    textEncoding={textEncoding}
                    westernScript={westernScript}
                    originalBody={state.originalValue}
                    currentValue={state.currentValue}
                    showErrorsByDefault={showErrorsByDefault}
                />

                {/* Action buttons */}
                <EncoderButtons
                    state={state}
                    role={role}
                    userId={session?.user?.id}
                    suggestedChange={suggestedChange}
                    canSubmit={actualCanSubmit}
                    canUpdate={actualCanUpdate}
                    canApprove={canApprove}
                    canCancel={canCancel}
                    canUndo={canUndo}
                    hasFormikErrors={hasFormErrors}
                    onSubmit={handleSubmit}
                    onUpdate={handleUpdate}
                    onApprove={handleApprove}
                    onCancel={handleCancelSubmission}
                    onUndo={handleUndo}
                />
            </VStack>
        </EncodingContainer>
    )
}
